import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity,
  Award,
  Building2,
  CalendarDays,
  CheckSquare,
  FileText,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  Network,
  StickyNote,
} from "lucide-react";
import { Card, Button } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { TabPlaceholderCard } from "@/components/DealFlow/common/TabPlaceholderCard";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { useSpvTrust, useUpdateSpvTrust } from "@/hooks/capitalMarkets/useSpvTrusts";
import { useSpvPoolList } from "@/hooks/capitalMarkets/useSpvPools";
import { useProviderList } from "@/hooks/capitalMarkets/useServiceProviders";
import { useEntityNameMap } from "@/hooks/capitalMarkets/useCounterpartyNames";
import type { SpvDto } from "@/types/spvPool";
import type { ServiceProviderType } from "@/types/serviceProviders";
import { TrustStatusBadge } from "../shared/components";
import { formatDate } from "../shared/format";
import { PoolStatusBadge } from "../shared/components";

type TabId =
  | "overview"
  | "pools"
  | "providers"
  | "certifications"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "pools",
  "providers",
  "certifications",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
];

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "pools", label: "Pools", icon: Network },
  { key: "providers", label: "Service Providers", icon: Building2 },
  { key: "certifications", label: "Certifications", icon: Award },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "tasks", label: "Tasks", icon: CheckSquare },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "communications", label: "Communications", icon: MessageCircle },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "activity", label: "Activity", icon: Activity },
];

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function TrustDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const trustQuery = useSpvTrust(id);
  const trust = trustQuery.data;

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  if (trustQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading trust…</p>
      </Card>
    );
  }

  if (!trust) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Trust not found</h2>
        <p className="text-small text-gray-mid">
          The SPV trust you requested was not found or you don&apos;t have
          permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/capital-markets/trusts")}
        >
          Back to trusts
        </button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex flex-wrap items-center gap-space-3">
        <h1 className="font-display font-bold text-h2 text-onyx m-0">
          {trust.trustName}
        </h1>
        <TrustStatusBadge status={trust.status} />
      </div>

      <EntityTabBar
        ariaLabel="Trust sections"
        testIdPrefix="spv-trust"
        items={TAB_ITEMS}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && <OverviewTab trust={trust} />}
        {tab === "pools" && <PoolsTab trustId={trust.id} />}
        {tab === "providers" && <ProvidersTab trust={trust} />}
        {tab === "certifications" && (
          <TabPlaceholderCard
            title="Certifications"
            body="Trust certifications will surface here once the certifications module is wired to SPV trusts."
          />
        )}
        {tab === "documents" && (
          <DocumentPanel
            ownerType="spv"
            ownerId={trust.id}
            rootName={trust.trustName}
          />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Spv"
            relatedEntityId={trust.id}
            addButtonLabel="+ Add task"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Spv"
            relatedEntityId={trust.id}
            entityLabel={trust.trustName}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="spv"
            resourceId={trust.id}
            entityLabel={trust.trustName}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="spv"
            ownerId={trust.id}
            ownerLabel={trust.trustName}
          />
        )}
        {tab === "notes" && (
          <NotesTabPanel ownerType="spv" ownerId={trust.id} />
        )}
        {tab === "activity" && (
          <ActivityTabPanel ownerType="spv" ownerId={trust.id} />
        )}
      </div>
    </div>
  );
}

function OverviewTab({ trust }: { trust: SpvDto }) {
  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
        Trust Identity
      </h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
        <Field label="Trust name" value={trust.trustName} />
        <Field label="Status" value={trust.status} />
        <Field label="State of formation" value={trust.stateOfFormation} />
        <Field label="Formation date" value={formatDate(trust.formationDate)} />
        <Field label="EIN" value={trust.einMasked ?? "—"} mono />
        <Field label="Governing law state" value={trust.governingLawState} />
      </dl>
      {trust.notes && (
        <p className="text-small text-gray-mid mt-space-3 mb-0 whitespace-pre-line">
          {trust.notes}
        </p>
      )}
    </Card>
  );
}

function PoolsTab({ trustId }: { trustId: string }) {
  const poolsQuery = useSpvPoolList({ spvId: trustId, pageSize: 200 });
  const pools = poolsQuery.data?.data ?? [];

  if (poolsQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading pools…</p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
        Pools in this Trust
      </h3>
      {pools.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No pools under this trust yet.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Pool Name</TH>
              <TH>Status</TH>
              <TH>Inception</TH>
            </TR>
          </THead>
          <TBody>
            {pools.map((p) => (
              <TR key={p.id}>
                <TD>
                  <Link
                    to={`/capital-markets/pools/${p.id}`}
                    className="font-semibold hover:text-gold-shadow"
                  >
                    {p.poolName}
                  </Link>
                </TD>
                <TD>
                  <PoolStatusBadge status={p.status} />
                </TD>
                <TD>{formatDate(p.inceptionDate)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

type ProviderField =
  | "indentureTrusteeId"
  | "trustCompanyId"
  | "primaryPlacementAgentId"
  | "backupServicerId";

interface ProviderCard {
  field: ProviderField;
  label: string;
  providerType: ServiceProviderType;
}

const PROVIDER_CARDS: ProviderCard[] = [
  {
    field: "indentureTrusteeId",
    label: "Indenture Trustee",
    providerType: "IndentureTrustee",
  },
  {
    field: "trustCompanyId",
    label: "Trust Company",
    providerType: "TrustCompany",
  },
  {
    field: "primaryPlacementAgentId",
    label: "Placement Agent",
    providerType: "PlacementAgent",
  },
  {
    field: "backupServicerId",
    label: "Backup Servicer",
    providerType: "BackupServicer",
  },
];

function ProvidersTab({ trust }: { trust: SpvDto }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Spv.Manage");

  const [editing, setEditing] = useState<ProviderCard | null>(null);
  const { map: entityNames } = useEntityNameMap();

  // Resolve each slot's current provider to a display name. Each provider's
  // identity lives on a linked Entity; the four directories are small.
  const trusteeList = useProviderList("IndentureTrustee", { pageSize: 200 });
  const trustCoList = useProviderList("TrustCompany", { pageSize: 200 });
  const agentList = useProviderList("PlacementAgent", { pageSize: 200 });
  const backupList = useProviderList("BackupServicer", { pageSize: 200 });

  const providerName = (
    type: ServiceProviderType,
    providerId: string | null,
  ): string | null => {
    if (!providerId) return null;
    const list =
      type === "IndentureTrustee"
        ? trusteeList.data?.data
        : type === "TrustCompany"
          ? trustCoList.data?.data
          : type === "PlacementAgent"
            ? agentList.data?.data
            : backupList.data?.data;
    const row = list?.find((p) => p.id === providerId);
    return row ? (entityNames.get(row.entityId) ?? "Linked provider") : null;
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-4">
        {PROVIDER_CARDS.map((c) => {
          const name = providerName(c.providerType, trust[c.field]);
          return (
            <Card key={c.field}>
              <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
                {c.label}
              </h3>
              <p className="text-small text-onyx m-0">
                {trust[c.field] ? (
                  name ? (
                    <span className="font-semibold">{name}</span>
                  ) : (
                    <span className="font-mono">{trust[c.field]}</span>
                  )
                ) : (
                  <span className="text-gray-mid italic">Not set</span>
                )}
              </p>
              {canManage && (
                <Button
                  variant="text"
                  size="sm"
                  className="mt-space-2"
                  onClick={() => setEditing(c)}
                >
                  Change
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {editing && (
        <ChangeProviderModal
          trustId={trust.id}
          card={editing}
          currentValue={trust[editing.field]}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function ChangeProviderModal({
  trustId,
  card,
  currentValue,
  onClose,
}: {
  trustId: string;
  card: ProviderCard;
  currentValue: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const update = useUpdateSpvTrust(trustId);
  const listQuery = useProviderList(card.providerType, { pageSize: 200 });
  const { map: entityNames } = useEntityNameMap();
  const [draft, setDraft] = useState(currentValue ?? "");

  const handleSave = () => {
    update
      .mutateAsync({ [card.field]: draft || null })
      .then(() => {
        toast.success(`${card.label} updated.`);
        onClose();
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Change ${card.label}`}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={update.isPending} onClick={handleSave}>
            Save
          </Button>
        </div>
      }
    >
      <p className="text-small text-gray-mid mb-space-2">
        Select the {card.label.toLowerCase()} for this trust.
      </p>
      <Select
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        aria-label={card.label}
      >
        <option value="">— Not set —</option>
        {(listQuery.data?.data ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {entityNames.get(p.entityId) ?? p.id} ({p.status})
          </option>
        ))}
      </Select>
    </Modal>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-label font-display uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className={`text-small text-onyx m-0 ${mono ? "font-mono" : ""}`}>
        {value}
      </dd>
    </div>
  );
}
